import React from "react";
import ReactDOM from "react-dom";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";

import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider as ThemeProviderV5, createTheme as createThemeV5 } from "@mui/material/styles";
import GlobalStyles from "@mui/material/GlobalStyles";
import themeColors from "assets/theme/colors.js";
import hexToRgb from "assets/theme/hex-to-rgb.js";

import { ClerkProvider } from '@clerk/clerk-react'
import { SnackbarProvider } from 'notistack';

import { Provider } from 'react-redux';
import store from "store/store.js";

import AdminLayout from "layouts/Admin.js";
import ModelLayout from "layouts/Models.js";
import * as amplitude from '@amplitude/analytics-browser';
import RedirectHandler from "components/utilities/RedirectHandler.js";
import ErrorBoundary from "components/utilities/ErrorBoundary.js";
import { captureAttribution } from "utils/attribution.js";
import { syncAttributionUserProps, ANALYTICS_DISABLED } from "utils/analytics.js";
import { initOpenAiPixel } from "utils/openaiPixel.js";
import { apiBase } from "functions/server/apiBase";
import { API_LANDING_ENABLED } from "utils/apiLanding";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";

import 'assets/styles/ag-theme-balham-single.css';
import 'assets/styles/argon.min.css'; 
import 'assets/styles/custom-table-styles.css'
import "assets/styles/data-dashboard.css";
import "assets/styles/data-entry-table.css";
import "assets/styles/google-auto-complete.css";
import "assets/styles/geomap.css";
import 'material-icons/iconfont/material-icons.css';
import 'assets/styles/antd.css';


// Capture ad attribution (utm_*/gclid/referrer) before anything can rewrite
// the URL — RedirectHandler and UseCasesPage both replace the query string
// after first render. Must stay above amplitude.init and the GA4 loader.
// (Runs even when analytics is disabled below: it's localStorage-only and
// needed to test attribution locally.)
captureAttribution();

/*
Automated and local-dev traffic must not reach analytics: the e2e/smoke suites
run against prod (each CI run minted a fresh "new user" — ~100 on 2026-08-18),
and once ad campaigns run a test sign-up would register a fake Google Ads
conversion. navigator.webdriver covers Playwright/Selenium; the hostname check
covers `npm start` against the real env keys.

The predicate now lives in utils/analytics.js, next to the calls it gates.
Skipping init here was never enough on its own: the SDK queues calls made
before init in memory, uncapped, so every track() in a local session was
retained for the life of the tab. analytics.js no-ops them at the source.
*/

if (!ANALYTICS_DISABLED) {
  // Session Replay is a separate plugin — autocapture does NOT include it.
  // Registered before init so the very first session is recorded. 100% sample
  // rate is deliberate while traffic is small (Starter plan: 1,000 replays/mo);
  // drop sampleRate if campaigns push past that. Inputs are masked by default.
  amplitude.add(sessionReplayPlugin({ sampleRate: 1 }));

  /*
  Stamp the captured first-touch / last-touch attribution (the ft_ and lt_
  props) onto the Amplitude user profile, so people — not just events — can be
  segmented by ad source.

  AWAITING init is the whole point. This used to be a bare call on the next
  line, and it silently lost most of its writes: over the 30 days to 4 Sep,
  53 users carried ft_landing_path as an EVENT property (track merges it in, and
  that path works) while only 14 had it as a USER property. So capture and
  storage were fine; the identify was being dropped.

  init() is asynchronous and returns an AmplitudeReturn. An identify issued
  before it settles races the SDK's setup, and adding the session-replay plugin
  above lengthened that setup — which is when the loss got bad. Chaining off
  .promise removes the race entirely.

  Consequence of the bug, for anyone reading the old data: ad performance could
  not be judged from user properties. Segmenting on our own ft_gclid found 10
  ad visitors where Amplitude's own initial_gclid found 67, and the 10 were the
  biased subset that happened to win the race — which made paid traffic look far
  better than it was.
  */
  amplitude
    .init(process.env.REACT_APP_AMPLITUDE_KEY, { autocapture: true })
    .promise.then(syncAttributionUserProps)
    .catch(() => {
      // Never let a failed init break boot. Attribution still rides on every
      // event via track(); only the user-profile mirror is lost.
    });
}

// Google Analytics 4 — loaded only when a measurement id is configured. This is
// an inert no-op until REACT_APP_GA4_ID is set (create a GA4 property, add the
// id to the env), at which point it activates on the next deploy. gtag() is then
// available on window and utils/analytics.js mirrors funnel events to it.
// OpenAI Ads measurement pixel — same shape as GA4 below: inert until
// REACT_APP_OPENAI_PIXEL_ID is set, so no third-party script reaches a visitor
// until we are actually advertising on that channel.
if (!ANALYTICS_DISABLED) {
  initOpenAiPixel();
}

const GA4_ID = process.env.REACT_APP_GA4_ID;
if (GA4_ID && !ANALYTICS_DISABLED) {
  const gaScript = document.createElement("script");
  gaScript.async = true;
  gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(gaScript);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", GA4_ID);
}


// check that the environment variables are set
const requiredEnvVars = [
  'REACT_APP_AMPLITUDE_KEY',
  'REACT_APP_BACK_END_PORT',
  'REACT_APP_BACK_END_SERVER',
  'REACT_APP_MAPBOX_API',
  'REACT_APP_AG_GRID_KEY',
  'REACT_APP_CLERK_PUBLISHABLE_KEY',
  "REACT_APP_DOCS_URL"
];

requiredEnvVars.forEach((envVar) => {
  if (!process.env[envVar]) {
    console.error(`Error: ${envVar} is not set`);
    throw new Error(`Missing environment variable: ${envVar}`);
  }
});

/*
check the health of the backend server

Via apiBase() like every other request, so a build aimed at a different backend
(the dev gateway) health-checks THAT backend rather than the one baked in at
build time. Reporting the wrong origin healthy is worse than not checking.

The env-var assertion above stays as it is: the runtime override is a testing
seam, not a way to build without configuration.
*/
const checkBackendHealth = async () => {
  try {
    const response = await fetch(`${apiBase()}/health`);
    if (!response.ok) {
      console.error(`Backend server is not healthy: ${response.statusText}`);
      throw new Error(`Backend server is not healthy: ${response.statusText}`);
    }
    const data = await response.json();
    if (data.data !== 'healthy') {
      console.error(`Backend server is not healthy: ${data.message}`);
      throw new Error(`Backend server is not healthy: ${data.message}`);
    }
    console.log('Backend server is healthy');
  }
  catch (error) {
    console.error(`Error checking backend health: ${error.message}`);
  }
};
checkBackendHealth()

const themeV5 = createThemeV5({
  palette: {
    ...themeColors,
    primary: {
      ...themeColors.primary,
      contrastText: '#fff',
    },
    secondary: {
      ...themeColors.secondary,
    },
    dark: {
      ...themeColors.dark,
    },
    text: {
      ...themeColors.text,
      primary: themeColors.text.primary,
    },
    buttonLightLabel: {
      main: "rgba(" + hexToRgb(themeColors.white.main) + ", 0.95)",
    },
    sidebarLinks: {
      // 0.5 resolves to #808080 — 3.95:1 on the drawer, under the AA floor for
      // the 13px nav labels. 0.6 reads as the same grey and clears it at 5.7:1.
      main: "rgba(" + hexToRgb(themeColors.black.main) + ", 0.6)",
      dark: "rgba(" + hexToRgb(themeColors.black.main) + ", 0.9)",
    },
    adminNavbarSearch: {
      main: "rgba(" + hexToRgb(themeColors.white.main) + ", 0.6)",
    },
    authNavbarLink: {
      main: "rgba(" + hexToRgb(themeColors.white.main) + ", 0.65)",
      dark: "rgba(" + hexToRgb(themeColors.white.main) + ", 0.95)",
    },
    dashboard: {
      background: {
        main: '#f4f6f8',
        exampleMode: '#fff7f5'
      }
    },
    card: {
      background: {
        main: '#ffffff'
      }
    }
  },
  typography: {
    fontFamily: "Open Sans,sans-serif",
    // Brand refresh R1 (per the refresh canvas, R1 board): headings move to
    // Hanken Grotesk — a display-grade cousin of Open Sans — with modest
    // negative tracking at hero sizes. Body copy stays Open Sans; components
    // that hardcode a family are untouched and follow in later boards.
    h1: { fontFamily: "Hanken Grotesk, Open Sans, sans-serif", letterSpacing: "-0.02em" },
    h2: { fontFamily: "Hanken Grotesk, Open Sans, sans-serif", letterSpacing: "-0.015em" },
    h3: { fontFamily: "Hanken Grotesk, Open Sans, sans-serif", letterSpacing: "-0.01em" },
    h4: { fontFamily: "Hanken Grotesk, Open Sans, sans-serif" },
    h5: { fontFamily: "Hanken Grotesk, Open Sans, sans-serif" },
    h6: { fontFamily: "Hanken Grotesk, Open Sans, sans-serif" },
  },

  // Brand refresh R4 (canvas board R4): flat, bordered, airy surfaces.
  // One radius token; contained buttons lose elevation and uppercase;
  // Cards trade the floating template shadow for a hairline + whisper.
  // Menus, popovers and dialogs keep their shadows - they genuinely float.
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 600 },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: "1px solid #e0e8e6",
          boxShadow: "0 1px 2px rgba(16, 48, 44, 0.06)",
          borderRadius: 10,
        },
      },
    },
  },
});

const app = (
    <ThemeProviderV5 theme={themeV5}>
  <Provider store={store}>
    <ErrorBoundary>
      <ClerkProvider publishableKey={process.env.REACT_APP_CLERK_PUBLISHABLE_KEY}
      >
    <CssBaseline /> {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
    <GlobalStyles styles={{
      '.MuiButton-iconSizeSmall > *': { fontSize: '1rem !important' },
      '.MuiButton-iconSizeMedium > *': { fontSize: '1.125rem !important' },
      '.MuiButton-iconSizeLarge > *': { fontSize: '1.25rem !important' },
      // Baseline visible keyboard-focus indicator (WCAG 2.4.7). :focus-visible
      // only fires for keyboard/programmatic focus, so mouse clicks don't get
      // outlines. Components with their own focus styling override this.
      '*:focus-visible': { outline: '2px solid #017C76', outlineOffset: '2px' },
    }} />
    <BrowserRouter>
      <SnackbarProvider maxSnack={3}  anchorOrigin={{vertical: 'top', horizontal: 'center'}}>
        <Switch>
          <Redirect exact from="/" to="/home" />
          <Route path="/home" render={(props) => <AdminLayout {...props} />} />
          {/* Old home URL — keep redirecting so existing links/bookmarks don't 404. */}
          <Redirect from="/index" to="/home" />
          <Route path="/admin" render={(props) => <AdminLayout {...props} />} />
          <Route path="/pricing" render={(props) => <AdminLayout {...props} />} />
          {/* Until the landing page is switched on, /api is a redirect out to
              the docs portal — which is what nav, sidebar and footer expect
              today. Flipping API_LANDING_ENABLED swaps it for a real page that
              links to the portal instead of jumping to it. */}
          {API_LANDING_ENABLED
            ? <Route path="/api" render={(props) => <AdminLayout {...props} />} />
            : <Route path="/api" render={() => { window.location.replace(process.env.REACT_APP_DOCS_URL); return null; }} />}
          <Route path="/careers" render={(props) => <AdminLayout {...props} />} />
          <Route path="/model-builder" render={(props) => <AdminLayout {...props} />} />
          <Route path="/use-cases" render={(props) => <AdminLayout {...props} />} />
          <Route path="/industries" render={(props) => <AdminLayout {...props} />} />
          <Route path="/terms-of-service" render={(props) => <AdminLayout {...props} />} />
          <Route path="/privacy-policy" render={(props) => <AdminLayout {...props} />} />
          <Route path="/models" render={(props) => <ModelLayout {...props} />} />
          <Route path="/examples" render={(props) => <ModelLayout {...props} />} />
          {/* Checkout is handled inline by Clerk's <PricingTable> on /pricing now. */}
          <Redirect from="/checkout" to="/pricing" />
          <Route path="/account" render={(props) => <AdminLayout {...props} />} />
          <Route path="/redirect" render={(props) => <RedirectHandler {...props} />} />
          <Route render={(props) => <AdminLayout {...props} />} />
        </Switch>
      </SnackbarProvider>
    </BrowserRouter>
    </ClerkProvider>
    </ErrorBoundary>
    </Provider>
    </ThemeProviderV5>
);

// react-snap prerenders the public pages to static HTML at build time. When that
// markup is already in #root, hydrate it; otherwise render fresh (this is the
// normal path in dev and in a build without prerendering, so it's a safe no-op
// until react-snap is activated).
const rootElement = document.querySelector("#root");

// Drop the first-paint placeholder from index.html once React has mounted.
// It's a sibling of #root (not a child) precisely so it doesn't interfere with
// the hydrate path above.
const removeLoadingScreen = () => {
  const el = document.getElementById("app-loading");
  if (el) el.remove();
};

if (rootElement.hasChildNodes()) {
  ReactDOM.hydrate(app, rootElement, removeLoadingScreen);
} else {
  ReactDOM.render(app, rootElement, removeLoadingScreen);
}
